import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/common/Layout';
import { bookService } from '../services/bookService';
import type { BulkUpload, BulkUploadStatus, BulkUploadUpdate } from '../types';
import { getImageUrl } from '../utils/imageHelper';
import { Notification } from '../components/common/Notification';
import './BulkUploadPage.css';

export const BulkUploadPage = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState<BulkUpload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BulkUploadStatus>('PENDING');
  const [selectedUpload, setSelectedUpload] = useState<BulkUpload | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [adminComment, setAdminComment] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [editFormData, setEditFormData] = useState<BulkUploadUpdate>({});
  const [isRetrying, setIsRetrying] = useState(false);
  const [isMarkingAsDone, setIsMarkingAsDone] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
  } | null>(null);

  // Image preview state
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ url: string; title: string } | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  useEffect(() => {
    loadUploads();
  }, [statusFilter]);

  // Pagination logic
  const totalPages = Math.ceil(uploads.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUploads = uploads.slice(startIndex, endIndex);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleImageClick = (imageUrl: string, title: string) => {
    setPreviewImage({ url: imageUrl, title });
    setShowImagePreview(true);
    setZoomLevel(1); // Reset zoom level when opening
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  const loadUploads = async () => {
    try {
      setIsLoading(true);
      const params: { skip?: number; limit?: number; status?: string } = { limit: 1000 };
      if (statusFilter) {
        params.status = statusFilter;
      }
      const data = await bookService.getAllBulkUploads(params);
      setUploads(data);
    } catch (error) {
      console.error('Error loading bulk uploads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (upload: BulkUpload) => {
    setSelectedUpload(upload);
    setShowDetailModal(true);
    setAdminComment(upload.admin_comment || '');
  };

  const handleEdit = (upload: BulkUpload) => {
    setSelectedUpload(upload);
    setEditFormData({
      title: upload.title,
      author_name: upload.author_name,
      isbn: upload.isbn || undefined,
      publisher: upload.publisher || undefined,
      publication_year: upload.publication_year ?? undefined,
      category: upload.category || undefined,
      description: upload.description || undefined,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUpload) return;

    setIsVerifying(true);

    try {
      // Send only changed fields with non-empty values
      const updatePayload: BulkUploadUpdate = {};
      if (editFormData.title && editFormData.title.trim()) {
        updatePayload.title = editFormData.title.trim();
      }
      if (editFormData.author_name && editFormData.author_name.trim()) {
        updatePayload.author_name = editFormData.author_name.trim();
      }
      if (editFormData.isbn && editFormData.isbn.trim()) {
        updatePayload.isbn = editFormData.isbn.trim();
      }
      if (editFormData.publisher && editFormData.publisher.trim()) {
        updatePayload.publisher = editFormData.publisher.trim();
      }
      if (editFormData.publication_year && editFormData.publication_year > 0) {
        updatePayload.publication_year = editFormData.publication_year;
      }
      if (editFormData.category && editFormData.category.trim()) {
        updatePayload.category = editFormData.category.trim();
      }
      if (editFormData.description && editFormData.description.trim()) {
        updatePayload.description = editFormData.description.trim();
      }

      // Check if there's anything to update
      if (Object.keys(updatePayload).length === 0) {
        setNotification({
          type: 'info',
          title: 'No Changes',
          message: 'No changes were made.',
        });
        setIsVerifying(false);
        setShowEditModal(false);
        setSelectedUpload(null);
        setEditFormData({});
        return;
      }

      console.log('Sending update payload:', updatePayload);
      await bookService.updateBulkUpload(selectedUpload.id, updatePayload);

      setNotification({
        type: 'success',
        title: 'Success!',
        message: `"${selectedUpload.title}" has been updated.`,
      });

      // Update the list with the edited data
      setUploads((prev) =>
        prev.map((u) =>
          u.id === selectedUpload.id
            ? { ...u, ...updatePayload }
            : u
        )
      );

      setShowEditModal(false);
      setSelectedUpload(null);
      setEditFormData({});
    } catch (error: any) {
      console.error('Error updating bulk upload:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update bulk upload. Please try again.';
      // Check for duplicate ISBN error
      if (errorMessage.includes('ISBN') && errorMessage.includes('already exists')) {
        setNotification({
          type: 'error',
          title: 'Duplicate ISBN',
          message: errorMessage,
        });
      } else {
        setNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setSelectedUpload(null);
    setEditFormData({});
  };

  const handleApprove = async (upload: BulkUpload) => {
    setIsVerifying(true);

    try {
      await bookService.verifyBulkUpload(upload.id, {
        status: 'APPROVED',
        admin_comment: null,
      });

      setNotification({
        type: 'success',
        title: 'Book Approved!',
        message: `"${upload.title}" has been added to the Book Table and marked as approved.`,
      });

      // Remove the approved item from the current list to update UI immediately
      setUploads((prev) => prev.filter((u) => u.id !== upload.id));
    } catch (error: any) {
      console.error('Error approving bulk upload:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to approve bulk upload. Please try again.';
      // Check for duplicate ISBN error
      if (errorMessage.includes('ISBN') && errorMessage.includes('already exists')) {
        setNotification({
          type: 'error',
          title: 'Duplicate ISBN',
          message: errorMessage,
        });
      } else {
        setNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleReject = (upload: BulkUpload) => {
    setSelectedUpload(upload);
    setConfirmAction('REJECT');
    setAdminComment('');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedUpload || !confirmAction) return;

    setIsVerifying(true);

    try {
      if (confirmAction === 'APPROVE' || confirmAction === 'REJECT') {
        await bookService.verifyBulkUpload(selectedUpload.id, {
          status: confirmAction === 'APPROVE' ? 'APPROVED' : 'REJECTED',
          admin_comment: adminComment || null,
        });

        // Show success notification
        setNotification({
          type: 'success',
          title: confirmAction === 'APPROVE' ? 'Book Approved!' : 'Book Rejected',
          message: confirmAction === 'APPROVE'
            ? `"${selectedUpload.title}" has been added to the Book Table and marked as approved.`
            : `"${selectedUpload.title}" has been rejected.`,
        });

        // Remove the item from all sections immediately (cascade delete)
        setUploads((prev) => prev.filter((u) => u.id !== selectedUpload.id));
      }

      setShowConfirmModal(false);
      setSelectedUpload(null);
      setConfirmAction(null);
      setAdminComment('');
    } catch (error: any) {
      console.error('Error verifying bulk upload:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to process bulk upload. Please try again.';
      // Check for duplicate ISBN error
      if (errorMessage.includes('ISBN') && errorMessage.includes('already exists')) {
        setNotification({
          type: 'error',
          title: 'Duplicate ISBN',
          message: errorMessage,
        });
      } else {
        setNotification({
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setSelectedUpload(null);
    setConfirmAction(null);
    setAdminComment('');
  };

  const handleRetry = async (upload: BulkUpload) => {
    setIsRetrying(true);

    try {
      const updatedUpload = await bookService.retryBulkUpload(upload.id);

      setNotification({
        type: 'success',
        title: 'Retry Successful!',
        message: `"${updatedUpload.title}" has been moved to Pending for review.`,
      });

      // Update the list with the full response from the retry endpoint
      // This replaces all fields with the newly extracted data
      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id ? updatedUpload : u
        )
      );
    } catch (error: any) {
      console.error('Error retrying bulk upload:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to retry bulk upload. Please try again.';
      setNotification({
        type: 'error',
        title: 'Retry Failed',
        message: errorMessage,
      });
      // Reload uploads to get the latest status
      await loadUploads();
    } finally {
      setIsRetrying(false);
    }
  };

  const handleRetryAll = async () => {
    setIsRetrying(true);

    try {
      const result = await bookService.retryAllErrorBulkUploads();

      if (result.success_count > 0 || result.failed_count > 0) {
        setNotification({
          type: result.success_count > 0 && result.failed_count === 0 ? 'success' : 'warning',
          title: result.success_count > 0 && result.failed_count === 0 ? 'All Retries Successful!' : 'Retry Completed',
          message: result.message,
        });
      } else {
        setNotification({
          type: 'info',
          title: 'No Uploads to Retry',
          message: 'There are no error uploads to retry.',
        });
      }

      // Reload uploads to get the updated data from successful retries
      await loadUploads();
    } catch (error: any) {
      console.error('Error retrying all bulk uploads:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to retry bulk uploads. Please try again.';
      setNotification({
        type: 'error',
        title: 'Retry Failed',
        message: errorMessage,
      });
    } finally {
      setIsRetrying(false);
    }
  };

  const handleToggleMarkAsDone = async (upload: BulkUpload) => {
    setIsMarkingAsDone(true);

    try {
      // Toggle: if already marked, unmark it; if not marked, mark it
      const newMarkedAsDoneState = !upload.marked_as_done;
      
      await bookService.markBulkUploadAsDone(upload.id, newMarkedAsDoneState);

      setNotification({
        type: 'success',
        title: newMarkedAsDoneState ? 'Marked as Fixed!' : 'Marked as Not Fixed',
        message: newMarkedAsDoneState 
          ? `"${upload.title}" has been marked as fixed and will appear at the end of the error list.`
          : `"${upload.title}" has been marked as not fixed.`,
      });

      // Update the upload in the list
      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id ? { ...u, marked_as_done: newMarkedAsDoneState } : u
        )
      );
    } catch (error: any) {
      console.error('Error toggling mark as done:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update. Please try again.';
      setNotification({
        type: 'error',
        title: 'Error',
        message: errorMessage,
      });
    } finally {
      setIsMarkingAsDone(false);
    }
  };

  const getStatusBadgeClass = (status: BulkUploadStatus) => {
    switch (status) {
      case 'PENDING':
        return 'status-pending';
      case 'APPROVED':
        return 'status-approved';
      case 'REJECTED':
        return 'status-rejected';
      case 'ERROR':
        return 'status-error';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: BulkUploadStatus) => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      case 'ERROR':
        return 'Error';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <Layout isAdmin>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bulk uploads...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout isAdmin>
      <div className="bulk-upload-page">
        {/* Top Back Button */}
        <div className="back-button-container">
          <button
            className="btn btn-ghost back-btn"
            onClick={() => navigate('/admin/books')}
            title="Back to Book Table"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            <span>Back to Book Table</span>
          </button>
        </div>

        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">Bulk Upload Verification</h1>
            <p className="page-subtitle">
              Review and approve books submitted via bulk upload
            </p>
          </div>
        </div>

        {/* Status Filters */}
        <div className="filters-bar">
          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === 'PENDING' ? 'active' : ''}`}
              onClick={() => setStatusFilter('PENDING')}
            >
              Pending
            </button>
            <button
              className={`filter-tab ${statusFilter === 'APPROVED' ? 'active' : ''}`}
              onClick={() => setStatusFilter('APPROVED')}
            >
              Approved
            </button>
            <button
              className={`filter-tab ${statusFilter === 'REJECTED' ? 'active' : ''}`}
              onClick={() => setStatusFilter('REJECTED')}
            >
              Rejected
            </button>
            <button
              className={`filter-tab ${statusFilter === 'ERROR' ? 'active' : ''}`}
              onClick={() => setStatusFilter('ERROR')}
            >
              Error
            </button>
          </div>
          {statusFilter === 'ERROR' && (
            <button
              className="btn btn-primary btn-sm retry-all-btn"
              onClick={handleRetryAll}
              disabled={isRetrying || paginatedUploads.length === 0}
            >
              <span className="material-symbols-outlined">{isRetrying ? 'progress_activity' : 'refresh'}</span>
              {isRetrying ? 'Retrying...' : 'Retry All'}
            </button>
          )}
        </div>

        {/* Uploads Grid */}
        <div className="uploads-grid">
          {paginatedUploads.length === 0 ? (
            <div className="empty-state-card">
              <span className="material-symbols-outlined">inbox</span>
              <h3>No bulk uploads found</h3>
              <p>
                {statusFilter
                  ? `No ${statusFilter} uploads`
                  : 'No books have been submitted for bulk upload yet'}
              </p>
            </div>
          ) : (
            paginatedUploads.map((upload) => (
              <div key={upload.id} className="upload-card" onClick={(e) => {
                // Don't open detail modal if clicking on action buttons
                if ((e.target as HTMLElement).closest('button')) {
                  return;
                }
                handleViewDetails(upload);
              }}>
                <div className="upload-card-header">
                  <div className="upload-id">#{upload.id}</div>
                  {upload.status === 'ERROR' && upload.marked_as_done ? (
                    <span className="status-badge status-fixed">Fixed</span>
                  ) : (
                    <span className={`status-badge ${getStatusBadgeClass(upload.status)}`}>
                      {getStatusLabel(upload.status)}
                    </span>
                  )}
                </div>

                <div className="upload-card-body">
                  <div className="upload-images">
                    {upload.barcode_image_path && (
                      <div className="image-wrapper">
                        <label>Barcode Image (User Uploaded)</label>
                        <img
                          src={getImageUrl(upload.barcode_image_path)}
                          alt="Barcode"
                          className="barcode-image"
                        />
                      </div>
                    )}
                    <div className="image-wrapper">
                      <label>Cover Image {upload.cover_image_path ? '(Extracted)' : '(Default)'}</label>
                      {upload.cover_image_path ? (
                        <img
                          src={getImageUrl(upload.cover_image_path)}
                          alt="Cover"
                          className="cover-image"
                          onError={(e) => {
                            const target = e.currentTarget as HTMLImageElement;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div className="book-cover-placeholder-large-card" style={{ display: upload.cover_image_path ? 'none' : 'flex' }} title={`${upload.title} by ${upload.author_name}`}>
                        <div className="placeholder-large-content">
                          <span className="placeholder-large-title">{upload.title}</span>
                          <span className="placeholder-large-author">{upload.author_name}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="upload-details">
                    <h3 className="upload-title">{upload.title}</h3>
                    <p className="upload-author">by {upload.author_name}</p>

                    {upload.status === 'ERROR' && upload.error_message && (
                      <div className="error-message-section">
                        <span className="material-symbols-outlined error-icon">error</span>
                        <strong>{upload.marked_as_done ? 'Fixed:' : 'Error:'}</strong>
                        <p className="error-text">{upload.error_message}</p>
                      </div>
                    )}
                    
                    {upload.status !== 'ERROR' && upload.isbn && (
                      <p className="upload-isbn">
                        <strong>ISBN:</strong> {upload.isbn}
                      </p>
                    )}
                    {upload.status !== 'ERROR' && upload.category && (
                      <p className="upload-category">
                        <strong>Category:</strong> {upload.category}
                      </p>
                    )}
                    {upload.status !== 'ERROR' && upload.publisher && (
                      <p className="upload-publisher">
                        <strong>Publisher:</strong> {upload.publisher}
                      </p>
                    )}
                    {upload.status !== 'ERROR' && upload.publication_year && (
                      <p className="upload-year">
                        <strong>Year:</strong> {upload.publication_year}
                      </p>
                    )}
                    {upload.status !== 'ERROR' && upload.description && (
                      <p className="upload-description">{upload.description}</p>
                    )}
                    {upload.admin_comment && (
                      <div className="admin-comment">
                        <strong>Admin Comment:</strong>
                        <p>{upload.admin_comment}</p>
                      </div>
                    )}
                    <p className="upload-date">
                      <strong>Uploaded:</strong> {new Date(upload.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="upload-card-actions">
                  {upload.status === 'PENDING' && (
                    <>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(upload);
                        }}
                      >
                        <span className="material-symbols-outlined">edit</span>
                        Edit
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApprove(upload);
                        }}
                      >
                        <span className="material-symbols-outlined">check</span>
                        Approve
                      </button>
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(upload);
                        }}
                      >
                        <span className="material-symbols-outlined">close</span>
                        Reject
                      </button>
                    </>
                  )}
                  {upload.status === 'ERROR' && (
                    <div className="upload-card-actions-error">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRetry(upload);
                        }}
                        disabled={isRetrying}
                      >
                        <span className="material-symbols-outlined">{isRetrying ? 'progress_activity' : 'refresh'}</span>
                        {isRetrying ? 'Retrying...' : 'Retry'}
                      </button>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleMarkAsDone(upload);
                        }}
                      >
                        <span className="material-symbols-outlined">
                          {upload.marked_as_done ? 'undo' : 'check_circle'}
                        </span>
                        {upload.marked_as_done ? 'Mark as Undone' : 'Mark as Done'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <p className="pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, uploads.length)} of {uploads.length} uploads
            </p>
            <div className="pagination-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <span className="material-symbols-outlined">chevron_left</span>
                Previous
              </button>
              <span className="page-indicator">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {showDetailModal && selectedUpload && (
          <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
            <div className="modal-content bulk-upload-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Bulk Upload Details</h2>
                <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="detail-images">
                  {selectedUpload.barcode_image_path && (
                    <div className="image-section">
                      <label>Barcode Image (User Uploaded)</label>
                      <img
                        src={getImageUrl(selectedUpload.barcode_image_path)}
                        alt="Barcode"
                        className="clickable"
                        onClick={() => handleImageClick(getImageUrl(selectedUpload.barcode_image_path), `${selectedUpload.title} - Barcode`)}
                        title="Click to enlarge"
                      />
                    </div>
                  )}
                  <div className="image-section">
                    <label>Cover Image {selectedUpload.cover_image_path ? '(Extracted from ISBN)' : '(Default Placeholder)'}</label>
                    {selectedUpload.cover_image_path ? (
                      <img
                        src={getImageUrl(selectedUpload.cover_image_path)}
                        alt="Cover"
                        className="book-cover-detail clickable"
                        onClick={() => handleImageClick(getImageUrl(selectedUpload.cover_image_path), `${selectedUpload.title} - Cover`)}
                        title="Click to enlarge"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const placeholder = target.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="book-cover-placeholder-detail" style={{ display: selectedUpload.cover_image_path ? 'none' : 'flex' }}>
                      <span className="material-symbols-outlined">menu_book</span>
                    </div>
                  </div>
                </div>
                <div className="detail-info">
                  <h3>{selectedUpload.title}</h3>
                  <p><strong>Author:</strong> {selectedUpload.author_name}</p>
                  
                  {selectedUpload.status === 'ERROR' && selectedUpload.error_message && (
                    <div className="error-message-section detail">
                      <span className="material-symbols-outlined error-icon">error</span>
                      <div>
                        <strong>{selectedUpload.marked_as_done ? 'Fixed:' : 'Error:'}</strong>
                        <p className="error-text">{selectedUpload.error_message}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedUpload.status !== 'ERROR' && selectedUpload.isbn && <p><strong>ISBN:</strong> {selectedUpload.isbn}</p>}
                  {selectedUpload.status !== 'ERROR' && selectedUpload.publisher && <p><strong>Publisher:</strong> {selectedUpload.publisher}</p>}
                  {selectedUpload.status !== 'ERROR' && selectedUpload.publication_year && <p><strong>Year:</strong> {selectedUpload.publication_year}</p>}
                  {selectedUpload.status !== 'ERROR' && selectedUpload.category && <p><strong>Category:</strong> {selectedUpload.category}</p>}
                  {selectedUpload.status !== 'ERROR' && selectedUpload.description && (
                    <div className="description-section">
                      <strong>Description:</strong>
                      <p>{selectedUpload.description}</p>
                    </div>
                  )}
                  <p><strong>Status:</strong> 
                    {selectedUpload.status === 'ERROR' && selectedUpload.marked_as_done ? (
                      <span className="status-badge status-fixed">Fixed</span>
                    ) : (
                      <span className={`status-badge ${getStatusBadgeClass(selectedUpload.status)}`}>{getStatusLabel(selectedUpload.status)}</span>
                    )}
                  </p>
                  {selectedUpload.admin_comment && (
                    <div className="admin-comment-section">
                      <strong>Admin Comment:</strong>
                      <p>{selectedUpload.admin_comment}</p>
                    </div>
                  )}
                  <p><strong>Uploaded:</strong> {new Date(selectedUpload.uploaded_at).toLocaleString()}</p>
                  {selectedUpload.reviewed_at && (
                    <p><strong>Reviewed:</strong> {new Date(selectedUpload.reviewed_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
              {selectedUpload.status === 'PENDING' && (
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleEdit(selectedUpload);
                    }}
                  >
                    <span className="material-symbols-outlined">edit</span>
                    Edit
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleApprove(selectedUpload);
                    }}
                  >
                    <span className="material-symbols-outlined">check</span>
                    Approve
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleReject(selectedUpload);
                    }}
                  >
                    <span className="material-symbols-outlined">close</span>
                    Reject
                  </button>
                </div>
              )}
              {selectedUpload.status === 'ERROR' && (
                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleRetry(selectedUpload);
                    }}
                    disabled={isRetrying}
                  >
                    <span className="material-symbols-outlined">{isRetrying ? 'progress_activity' : 'refresh'}</span>
                    {isRetrying ? 'Retrying...' : 'Retry'}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={() => {
                      setShowDetailModal(false);
                      handleToggleMarkAsDone(selectedUpload);
                    }}
                  >
                    <span className="material-symbols-outlined">
                      {selectedUpload.marked_as_done ? 'undo' : 'check_circle'}
                    </span>
                    {selectedUpload.marked_as_done ? 'Mark as Undone' : 'Mark as Done'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Admin Comment Modal for Approve */}
        {showConfirmModal && selectedUpload && confirmAction === 'APPROVE' && (
          <div className="modal-overlay comment-modal-overlay" onClick={handleCancelAction}>
            <div className="modal-content comment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Approve Book</h2>
                <button className="modal-close" onClick={handleCancelAction}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="modal-message">
                  Approve "{selectedUpload.title}" and add to the Book Table?
                </p>
                <div className="comment-input-wrapper">
                  <label htmlFor="confirmComment">Admin Comment (optional)</label>
                  <textarea
                    id="confirmComment"
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={4}
                    className="form-textarea"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={handleCancelAction}>
                  Cancel
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleConfirmAction}
                >
                  <span className="material-symbols-outlined">check</span>
                  Approve & Add to Books
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin Comment Modal for Reject */}
        {showConfirmModal && selectedUpload && confirmAction === 'REJECT' && (
          <div className="modal-overlay comment-modal-overlay" onClick={handleCancelAction}>
            <div className="modal-content comment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Reject Book</h2>
                <button className="modal-close" onClick={handleCancelAction}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="modal-message">
                  Reject "{selectedUpload.title}"?
                </p>
                <div className="comment-input-wrapper">
                  <label htmlFor="confirmComment">Admin Comment (optional)</label>
                  <textarea
                    id="confirmComment"
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={4}
                    className="form-textarea"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={handleCancelAction}>
                  Cancel
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleConfirmAction}
                >
                  <span className="material-symbols-outlined">close</span>
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {showEditModal && selectedUpload && (
          <div className="modal-overlay" onClick={handleCancelEdit}>
            <div className="modal-content bulk-upload-edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Edit Bulk Upload</h2>
                <button className="modal-close" onClick={handleCancelEdit}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="edit-form">
                  <div className="form-group">
                    <label htmlFor="edit-title">Title *</label>
                    <input
                      id="edit-title"
                      type="text"
                      value={editFormData.title || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-author">Author Name *</label>
                    <input
                      id="edit-author"
                      type="text"
                      value={editFormData.author_name || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, author_name: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-isbn">ISBN</label>
                    <input
                      id="edit-isbn"
                      type="text"
                      value={editFormData.isbn || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, isbn: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-publisher">Publisher</label>
                    <input
                      id="edit-publisher"
                      type="text"
                      value={editFormData.publisher || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, publisher: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-year">Publication Year</label>
                    <input
                      id="edit-year"
                      type="number"
                      value={editFormData.publication_year || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, publication_year: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="form-input"
                      min="1000"
                      max="2100"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-category">Category</label>
                    <input
                      id="edit-category"
                      type="text"
                      value={editFormData.category || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                      className="form-input"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label htmlFor="edit-description">Description</label>
                    <textarea
                      id="edit-description"
                      value={editFormData.description || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      className="form-textarea"
                      rows={4}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={handleCancelEdit}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={isVerifying || !editFormData.title || !editFormData.author_name}
                >
                  {isVerifying ? (
                    <>
                      <span className="material-symbols-outlined loading-icon">progress_activity</span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined">save</span>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {notification && (
          <Notification
            type={notification.type}
            title={notification.title}
            message={notification.message}
            onClose={() => setNotification(null)}
          />
        )}

        {/* Image Preview Modal */}
        {showImagePreview && previewImage && (
          <div className="image-preview-overlay" onClick={() => setShowImagePreview(false)}>
            <div className="image-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="image-preview-header">
                <h3>{previewImage.title}</h3>
                <div className="zoom-controls">
                  <button
                    className="zoom-btn"
                    onClick={handleZoomOut}
                    title="Zoom Out"
                  >
                    <span className="material-symbols-outlined">remove</span>
                  </button>
                  <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    className="zoom-btn"
                    onClick={handleZoomIn}
                    title="Zoom In"
                  >
                    <span className="material-symbols-outlined">add</span>
                  </button>
                  <button
                    className="zoom-btn reset"
                    onClick={handleResetZoom}
                    title="Reset Zoom"
                  >
                    <span className="material-symbols-outlined">fullscreen</span>
                  </button>
                  <button
                    className="modal-close"
                    onClick={() => setShowImagePreview(false)}
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>
              </div>
              <div className="image-preview-content">
                <img
                  src={previewImage.url}
                  alt="Preview"
                  style={{ transform: `scale(${zoomLevel})` }}
                />
              </div>
              <div className="image-preview-footer">
                <a
                  href={previewImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                >
                  <span className="material-symbols-outlined">open_in_new</span>
                  Open in New Tab
                </a>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowImagePreview(false)}
                >
                  <span className="material-symbols-outlined">close</span>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
